import React from 'react';
import { Page, Text, View, Document, StyleSheet, Font } from '@react-pdf/renderer';
import { formatToCLP } from '../services/monedaService';

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    color: '#333333',
    backgroundColor: '#ffffff',
  },
  header: {
    marginBottom: 20,
    borderBottom: 1,
    borderBottomColor: '#3498db',
    paddingBottom: 10,
  },
  title: {
    fontSize: 18,
    marginBottom: 5,
    color: '#2c3e50',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 14,
    marginBottom: 5,
    color: '#3498db',
    textAlign: 'center',
  },
  section: {
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 12,
    marginBottom: 8,
    color: '#2c3e50',
    backgroundColor: '#ecf0f1',
    padding: 5,
  },
  row: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#ecf0f1',
    paddingVertical: 3,
    marginBottom: 3,
  },
  column: {
    flex: 1,
    paddingHorizontal: 5,
  },
  label: {
    fontSize: 9,
    color: '#7f8c8d',
  },
  value: {
    fontSize: 10,
    color: '#2c3e50',
  },
  table: {
    display: 'table',
    width: 'auto',
    marginVertical: 10,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#ecf0f1',
  },
  tableHeader: {
    backgroundColor: '#3498db',
  },
  tableHeaderText: {
    fontSize: 10,
    color: '#ffffff',
    padding: 5,
    flex: 1,
    textAlign: 'center',
  },
  tableCell: {
    fontSize: 9,
    padding: 5,
    flex: 1,
    textAlign: 'center',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: 'center',
    fontSize: 8,
    color: '#7f8c8d',
  },
  total: {
    marginTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#3498db',
    paddingTop: 5,
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  totalLabel: {
    fontSize: 11,
    marginRight: 10,
  },
  totalValue: {
    fontSize: 11,
    color: '#3498db',
  }
});

const ContractPDF = ({ formData, linesOfBusiness, cuotasData }) => {
  // Calcular el total general de todos los servicios
  const calculateTotalAmount = () => {
    return linesOfBusiness.reduce((total, line) => {
      return total + line.contractServices.reduce((serviceTotal, service) => {
        return serviceTotal + (service.amount || 0);
      }, 0);
    }, 0);
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Encabezado */}
        <View style={styles.header}>
         <Text style={styles.title}>FICHA DE ENTREVISTA</Text> 
        {/*   <Text style={styles.subtitle}>FICHA DE ENTREVISTA</Text> */}
          <Text style={[styles.value, { textAlign: 'center' }]}>
            N° Contrato: {formData.numContrato}
          </Text>
        </View>

        {/* Datos del Cliente */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>DATOS DEL CLIENTE</Text>
          <View style={styles.row}>
            <View style={styles.column}>
              <Text style={styles.label}>Identificador:</Text>
              <Text style={styles.value}>{formData.identifier.trim()}</Text>
            </View>
            <View style={styles.column}>
              <Text style={styles.label}>Nombre:</Text>
              <Text style={styles.value}>{`${formData.firstName} ${formData.lastName}`}</Text>
            </View>
          </View>
          <View style={styles.row}>
            <View style={styles.column}>
              <Text style={styles.label}>Email:</Text>
              <Text style={styles.value}>{formData.email}</Text>
            </View>
            <View style={styles.column}>
              <Text style={styles.label}>Fecha Contrato:</Text>
              <Text style={styles.value}>
                {formData.fechaContrato ? new Date(formData.fechaContrato).toLocaleDateString() : 'N/A'}
              </Text>
            </View>
          </View>
        </View>

        {/* Líneas de Negocio y Servicios */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>SERVICIOS CONTRATADOS</Text>
          {linesOfBusiness.map((line, index) => (
            <View key={index} style={{ marginBottom: 10 }}>
              <Text style={[styles.label, { marginBottom: 5 }]}>
                Línea de Negocio: {line.lineOfBusinessName}
              </Text>
              <View style={styles.table}>
                <View style={[styles.tableRow, styles.tableHeader]}>
                  <Text style={styles.tableHeaderText}>Servicio</Text>
                  <Text style={styles.tableHeaderText}>Monto</Text>
                </View>
                {line.contractServices.map((service, serviceIndex) => (
                  <View key={serviceIndex} style={styles.tableRow}>
                    <Text style={styles.tableCell}>{service.serviceName}</Text>
                    <Text style={styles.tableCell}>{formatToCLP(service.amount)}</Text>
                  </View>
                ))}
              </View>
            </View>
          ))}
        </View>

        {/* Cuotas */}
        {cuotasData.map((group, index) => (
          <View key={index} style={styles.section}>
            <Text style={styles.sectionTitle}>PLAN DE PAGOS</Text>
            <View style={styles.row}>
              <View style={styles.column}>
                <Text style={styles.label}>Método de Pago:</Text>
                <Text style={styles.value}>{group.metodoPagoName}</Text>
              </View>
              <View style={styles.column}>
                <Text style={styles.label}>Número de Cuotas:</Text>
                <Text style={styles.value}>{group.numeroCuotas}</Text>
              </View>
            </View>
            
            <View style={styles.table}>
              <View style={[styles.tableRow, styles.tableHeader]}>
                <Text style={styles.tableHeaderText}>N° Cuota</Text>
                <Text style={styles.tableHeaderText}>Fecha</Text>
                <Text style={styles.tableHeaderText}>Monto</Text>
              </View>
              {group.pagoCuotas.map((cuota, cuotaIndex) => (
                <View key={cuotaIndex} style={styles.tableRow}>
                  <Text style={styles.tableCell}>{cuota.numeroCuota}</Text>
                  <Text style={styles.tableCell}>
                    {new Date(cuota.fechaCuota).toLocaleDateString()}
                  </Text>
                  <Text style={styles.tableCell}>{formatToCLP(cuota.monto)}</Text>
                </View>
              ))}
            </View>
          </View>
        ))}

        {/* Total */}
        <View style={styles.total}>
          <Text style={styles.totalLabel}>MONTO TOTAL:</Text>
          <Text style={styles.totalValue}>{formatToCLP(calculateTotalAmount())}</Text>
        </View>

        {/* Observaciones */}
        {formData.observaciones && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>OBSERVACIONES</Text>
            <Text style={styles.value}>{formData.observaciones}</Text>
          </View>
        )}

        {/* Footer */}
        <Text style={styles.footer}>
          Este documento es una ficha informativa del contrato. Para más información, contacte a nuestro servicio de atención al cliente.
        </Text>
      </Page>
    </Document>
  );
};

export default ContractPDF;